import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { env } from "../config/env";
import { parseApiError } from "../utils/parseApiError";

const instance = axios.create({
  baseURL: env.API_URL,
  withCredentials: true,
});

// Callbacks wired by App at boot time. Keeping them out of this module
// (no `import store`) breaks the cycle that the old implementation had
// (axiosClient → store → authSlice → auth.thunk → auth.service → axiosClient).
type RefreshFn = () => Promise<void>;
type LogoutFn = () => void;
/**
 * Returns `true` when a refresh attempt is worth making. The refresh
 * token lives in an httpOnly cookie that JS can't read, so we can't
 * check the cookie directly — instead the app answers from its known
 * auth state ("am I logged in, or still bootstrapping?"). When this
 * returns `false` we KNOW there's no session, so we skip the refresh
 * round-trip and let the original request's 401 surface.
 */
type ShouldRefreshFn = () => boolean;

let onRefreshNeeded: RefreshFn | null = null;
let onUnauthorized: LogoutFn | null = null;
let shouldAttemptRefresh: ShouldRefreshFn | null = null;

export function registerAuthCallbacks(opts: {
  onRefreshNeeded: RefreshFn;
  onUnauthorized: LogoutFn;
  shouldAttemptRefresh?: ShouldRefreshFn;
}) {
  onRefreshNeeded = opts.onRefreshNeeded;
  onUnauthorized = opts.onUnauthorized;
  shouldAttemptRefresh = opts.shouldAttemptRefresh ?? null;
}

let isRefreshing = false;
// Queue of resolvers/rejectors for requests deferred while a refresh
// is in flight. The resolver signature must match what `new Promise`
// hands us (`value: T | PromiseLike<T>`), not the loose "optional T"
// shape — TS strict mode catches the mismatch.
type QueueEntry = {
  resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;
  reject: (error: unknown) => void;
};
let failedQueue: QueueEntry[] = [];

function processQueue(error: unknown = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    // resolve(undefined) is the contract for "the refresh succeeded;
    // retry your original request" — the caller's `.then(() => instance(originalRequest))`
    // doesn't read the resolved value.
    else resolve(undefined as unknown as AxiosResponse);
  });
  failedQueue = [];
}

// URLs that must NEVER trigger the refresh-on-401 flow. A 401 from any
// of these is a real failure (bad creds, expired link, no session) and
// trying to refresh would either loop or mask the real error.
const NO_REFRESH_PATHS = [
  "/auth/refresh",
  "/auth/logout",
  "/auth/logout-all",
  "/auth/login",
  "/auth/register",
  "/auth/password/forgot",
  "/auth/password/reset",
];

function isNoRefreshPath(url: string | undefined): boolean {
  if (!url) return false;
  return NO_REFRESH_PATHS.some((path) => url.includes(path));
}

let interceptorsAttached = false;

export function setInterceptors() {
  // Idempotent: in dev with HMR, calling twice would stack 401-handlers
  // and produce N parallel refresh attempts on a single failure.
  if (interceptorsAttached) return;
  interceptorsAttached = true;

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | (AxiosRequestConfig & { _retry?: boolean; skipAuthRefresh?: boolean })
        | undefined;

      if (!originalRequest) return Promise.reject(error);

      if (
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.skipAuthRefresh ||
        isNoRefreshPath(originalRequest.url)
      ) {
        return Promise.reject(error);
      }

      // Auth not yet bootstrapped to a logged-in state, or callbacks
      // never registered: don't try to refresh, just bubble up.
      if (!onRefreshNeeded) {
        return Promise.reject(error);
      }

      // No session to refresh (definitively logged out): skip the
      // pointless /auth/refresh round-trip and let the original 401
      // surface. We can't read the httpOnly refresh cookie, so this
      // defers to the app's auth state via the registered predicate.
      if (shouldAttemptRefresh && !shouldAttemptRefresh()) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => instance(originalRequest));
      }

      isRefreshing = true;
      try {
        await onRefreshNeeded();
        processQueue();
        return await instance(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr);
        if (onUnauthorized) onUnauthorized();
        throw refreshErr;
      } finally {
        isRefreshing = false;
      }
    },
  );
}

// Attach interceptors once at module load. Components must NOT call
// setInterceptors() again — the idempotency guard makes it safe, but
// calling it from render is a smell.
setInterceptors();

export class AxiosClient {
  get instance(): AxiosInstance {
    return instance;
  }

  protected get<Request = any, Response = any>(
    path: string,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    config = {
      ...(config || {}),
      headers: new AxiosHeaders({
        ...(config?.headers || {}),
      } as AxiosHeaders),
    };
    return this.instance.get<Response>(path, config);
  }

  protected post<Request = any, Response = any>(
    path: string,
    data?: Request,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    config = {
      ...(config || {}),
      headers: new AxiosHeaders({
        ...(config?.headers || {}),
      } as AxiosHeaders),
    };
    return this.instance.post<Response>(path, data, config);
  }

  protected put<Request = any, Response = any>(
    path: string,
    data?: Request,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    config = {
      ...(config || {}),
      headers: new AxiosHeaders({
        ...(config?.headers || {}),
      } as AxiosHeaders),
    };
    return this.instance.put<Response>(path, data, config);
  }

  protected delete<Request = any, Response = any>(
    path: string,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    config = {
      ...(config || {}),
      headers: new AxiosHeaders({
        ...(config?.headers || {}),
      } as AxiosHeaders),
    };
    return this.instance.delete<Response>(path, config);
  }
}

export class AuthAxiosService extends AxiosClient {
  protected async getAuth<Request = any, Response = any>(
    path: string,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    try {
      config = {
        ...(config || {}),
        headers: new AxiosHeaders({
          ...(config?.headers || {}),
        } as AxiosHeaders),
      };
      return await this.instance.get<Response>(path, config);
    } catch (err) {
      if (err instanceof AxiosError) {
        throw this.parseError(err);
      } else {
        throw err;
      }
    }
  }

  protected async postAuth<Request = any, Response = any>(
    path: string,
    data?: Request,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    try {
      config = {
        ...(config || {}),
        headers: new AxiosHeaders({
          ...(config?.headers || {}),
        } as AxiosHeaders),
      };
      return await this.instance.post<Response>(path, data, config);
    } catch (err) {
      if (err instanceof AxiosError) {
        throw this.parseError(err);
      } else {
        throw err;
      }
    }
  }

  protected async putAuth<Request = any, Response = any>(
    path: string,
    data?: Request,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    try {
      config = {
        ...(config || {}),
        headers: new AxiosHeaders({
          ...(config?.headers || {}),
        } as AxiosHeaders),
      };
      return await this.instance.put<Response>(path, data, config);
    } catch (err) {
      if (err instanceof AxiosError) {
        throw this.parseError(err);
      } else {
        throw err;
      }
    }
  }

  protected async deleteAuth<Request = any, Response = any>(
    path: string,
    config?: AxiosRequestConfig<Request>,
  ): Promise<AxiosResponse<Response>> {
    try {
      config = {
        ...(config || {}),
        headers: new AxiosHeaders({
          ...(config?.headers || {}),
        } as AxiosHeaders),
      };
      return await this.instance.delete<Response>(path, config);
    } catch (err) {
      if (err instanceof AxiosError) {
        throw this.parseError(err);
      } else {
        throw err;
      }
    }
  }

  private parseError(error: AxiosError): Error {
    // Convert raw AxiosError into the typed `ApiError` the rest of the
    // app expects (via parseApiError). A Phase 1 oversight left this
    // as a passthrough, so consumers doing `err instanceof ApiError`
    // always saw `false` and fell through to generic error branches.
    return parseApiError(error);
  }
}
export { instance };
